import { useEffect, useState } from "react";

/**
 * Renders a large list in progressive chunks so the initial paint stays
 * responsive even when the list has 1000+ items. The first `initialCount`
 * items render synchronously and the rest are appended in `chunkSize`
 * batches during `requestIdleCallback` (or a `setTimeout` fallback).
 *
 * Drop-in replacement for `items.map(renderItem)`:
 *
 *     <ProgressiveList items={achievements} renderItem={renderAchievement} />
 *
 * The visible window is sticky across `items` array-reference changes so a
 * background refetch (e.g. after saving a single achievement/character
 * unlock) never yanks the user's scroll position back to the top of the
 * list. `visible` only shrinks when the underlying data actually shrinks.
 */
export default function ProgressiveList({
  items,
  renderItem,
  initialCount = 60,
  chunkSize = 40,
  chunkDelayMs = 16,
}) {
  const [visible, setVisible] = useState(() => Math.min(initialCount, items.length));

  // When the items array reference changes (e.g. save refetch), do NOT
  // collapse the visible window back to `initialCount` — that would yank
  // the user's scroll position. Just clamp `visible` down if the list
  // actually shrank so we never render past the end.
  useEffect(() => {
    setVisible((v) => Math.min(Math.max(v, initialCount), items.length));
  }, [items, initialCount]);

  useEffect(() => {
    if (visible >= items.length) return undefined;

    const idle = window.requestIdleCallback
      ? window.requestIdleCallback
      : (cb) => window.setTimeout(cb, chunkDelayMs);
    const cancel = window.cancelIdleCallback
      ? window.cancelIdleCallback
      : window.clearTimeout;

    const handle = idle(() => {
      setVisible((v) => Math.min(items.length, v + chunkSize));
    });
    return () => cancel(handle);
  }, [visible, items.length, chunkSize, chunkDelayMs]);

  const slice = visible >= items.length ? items : items.slice(0, visible);
  return <>{slice.map(renderItem)}</>;
}
